// coverageLogic.ts — Coverage-Matrix Wave 2 (UI-A, AI-kh9r) PURE UI logic.
// No React, no network. Unit-tested in coverageLogic.test.ts. Binds Squad CE's
// completeness engine output (green/yellow/red) to the interactive matrix +
// drill + territory-panel rendering rules (spec §2/§3/§4).

import type {
  CoverageColorV2,
  MatchTypeV2,
  AssignmentInputV2,
  RouterStaffGraph,
} from '@/api/leadRouter';

// ── Completeness color tokens (spec §2) ──────────────────────────────────────
// green = fully assigned · yellow = partial OR broad/catch-all only · red = none.
export const COVERAGE_FILL: Record<CoverageColorV2, string> = {
  green: 'var(--forest-100, #d6ecd9)',
  yellow: 'var(--warn-bg, #f7eccf)',
  red: 'repeating-linear-gradient(45deg, #f6d5d5, #f6d5d5 5px, #fbe4e4 5px, #fbe4e4 10px)',
};
export const COVERAGE_INK: Record<CoverageColorV2, string> = {
  green: 'var(--forest, #2f7d3b)',
  yellow: 'var(--warn, #a5741a)',
  red: 'var(--danger, #b5433a)',
};
export const COVERAGE_GLYPH: Record<CoverageColorV2, string> = {
  green: '●',
  yellow: '◐',
  red: '—',
};

// ── Header counters ──────────────────────────────────────────────────────────
export interface CoverageCounts { full: number; partial: number; gaps: number; total: number }

export function matrixCounters(cells: ReadonlyArray<{ cell: CoverageColorV2 }>): CoverageCounts {
  let full = 0, partial = 0, gaps = 0;
  for (const c of cells) {
    if (c.cell === 'green') full++;
    else if (c.cell === 'yellow') partial++;
    else gaps++;
  }
  return { full, partial, gaps, total: cells.length };
}

// ── Overlap badge (spec §2 flag #3 — layered on the completeness fill) ────────
export interface OverlapBadge { show: boolean; kind: 'exec' | 'manager' | 'both' | null }

export function overlapBadge(o: { exec: boolean; manager: boolean }): OverlapBadge {
  if (o.exec && o.manager) return { show: true, kind: 'both' };
  if (o.manager) return { show: true, kind: 'manager' };
  if (o.exec) return { show: true, kind: 'exec' };
  return { show: false, kind: null };
}

// ── Drill / breadcrumb state (spec §3) ───────────────────────────────────────
export type DrillLevel = 'state' | 'district' | 'pincode';

export interface DrillPath {
  productGroupKey: string;
  productLabel?: string;
  state?: string;      // set once drilled into a state
  district?: string;   // set once drilled into a district
}

export interface Crumb { label: string; level: 'root' | 'state' | 'district' }

/** Breadcrumb items for the current drill path (root ▸ state ▸ district). */
export function buildBreadcrumb(path: DrillPath | null): Crumb[] {
  const crumbs: Crumb[] = [{ label: 'All states', level: 'root' }];
  if (!path) return crumbs;
  if (path.state) crumbs.push({ label: path.state, level: 'state' });
  if (path.district) crumbs.push({ label: path.district, level: 'district' });
  return crumbs;
}

// ── Cell → territory (matchType/matchValue) mapping ──────────────────────────
export interface Territory { matchType: MatchTypeV2; matchValue: string }

/** A cell at drill `level` for `unit` maps to a matchType/matchValue territory. */
export function cellTerritory(level: DrillLevel, unit: string): Territory {
  return { matchType: level as MatchTypeV2, matchValue: unit };
}

// ── Assign-exec payload builder (spec §4) ────────────────────────────────────
export function buildAssignExecPayload(input: {
  userId: string; productGroupKey: string; matchType: MatchTypeV2; matchValue: string;
}): AssignmentInputV2 {
  return {
    layer: 'EXEC',
    userId: input.userId,
    dealerId: null,
    productGroupKey: input.productGroupKey,
    matchType: input.matchType,
    matchValue: input.matchValue,
    deny: false,
    primary: false,
  };
}

// ── Manager detection + escalation target (spec §4 escalation-on-assign) ──────
/** A user is a manager iff they lead a team in the staff graph. */
export function isManager(userId: string, staffGraph: RouterStaffGraph): boolean {
  return Object.prototype.hasOwnProperty.call(staffGraph.execByManager ?? {}, userId);
}

/**
 * The rep's own team manager (escalation target), or null if they have none.
 * Bug-3 fix: resolve via the STRUCTURAL team map first (Team.managerId +
 * primaryTeam/membership) — the same source decideRouteV2 escalates through — and
 * fall back to the legacy reportsTo-based managerByExec only when the structural map
 * has no entry. A rep with no manager in EITHER map genuinely has none → null (the
 * prompt is correctly skipped, per spec §4).
 */
export function escalationTargetForRep(userId: string, staffGraph: RouterStaffGraph): string | null {
  return staffGraph.teamManagerByUser?.[userId] ?? staffGraph.managerByExec?.[userId] ?? null;
}
